<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Un tour virtual de Google Street View sitúa tu negocio en Google
            Maps — y en cada resultado de búsqueda que muestra tu ficha. Cuando
            un cliente potencial busca tu categoría de negocio cerca de él, tu
            tour interactivo 360° aparece directamente en los resultados,
            aumentando las tasas de clics y generando confianza antes de que te
            visite.
          </p>
          <p>
            Las imágenes quedan en <em>tu</em> lugar de Google Maps.
            <strong>Travvir es el editor de Street View</strong> en ese sitio —
            sin iniciar sesión con tu cuenta Google personal.
          </p>
          <p>
            Un <strong>escaneo único</strong>: elige el lugar en Maps, selecciona
            escenas y publica. Un <strong>tour 360°</strong>: elige el lugar y
            publica el tour directo. Detalles del producto en la
            <a :href="`${mainSiteUrl}/google-streetview`"
              >página de Google Street View</a
            >; los pasos en la app en
            <a href="/blog/es/publicar-google-street-view"
              >publicar con Travvir</a
            >.
          </p>
        </section>

        <section>
          <h2>¿Qué es un tour virtual de Google Street View?</h2>
          <p>
            Un tour de negocios de Google Street View es un tour virtual 360°
            publicado en Google Maps a través de tu Google Business Profile.
            Aparece en la Búsqueda de Google, Google Maps y el Panel de
            Conocimiento — ofreciendo a los usuarios una vista inmersiva del
            interior de tu negocio desde el buscador más grande del mundo.
          </p>
        </section>

        <section>
          <h2>
            Beneficios de SEO de los tours virtuales de Google Street View
          </h2>
          <p>
            Los negocios con tours virtuales de Google registran
            <strong>2× más engagement</strong> en su Google Business Profile. El
            tour aumenta el tiempo en la ficha, señala a Google que tu perfil
            está completo y es confiable, y puede influir positivamente en tu
            posición en los resultados locales.
          </p>
        </section>

        <section>
          <h2>Cómo crear un tour virtual de Google Street View</h2>
          <h3>Opción 1 — Capturar con Travvir, publicar en Google Street View</h3>
          <p>
            Crea un escaneo o un tour 360° enlazado en Travvir y usa
            <strong>Compartir → Google Street View</strong>. Empieza eligiendo el
            lugar correcto en Google Maps. En un escaneo único, selecciona las
            escenas y publica. En un tour, publica directamente. Travvir es el
            editor en ese sitio.
          </p>
          <h3>Opción 2 — Usar directamente la app Street View</h3>
          <p>
            Descarga la app Google Street View, toca el icono de cámara y sigue
            el proceso de captura guiado. Más sencillo, pero sin la mejora de
            calidad con IA de Travvir ni la vinculación multi-habitación.
          </p>
          <h3>Proceso de subida paso a paso</h3>
          <p>
            1. Abre el Gestor de Google Business Profile. 2. Selecciona "Fotos"
            y luego "Fotos 360°". 3. Sube tus imágenes equirectangulares (mínimo
            7,5 megapíxeles). 4. Coloca el marcador de ubicación con precisión.
            5. Envía para revisión — aprobado normalmente en 48 horas. 6. Una
            vez aprobado, vincula el tour desde la sección "Tour virtual" de tu
            perfil.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li>
            <a :href="`${mainSiteUrl}/google-streetview`"
              >Google Street View con Travvir</a
            >
          </li>
          <li>
            <a href="/blog/es/publicar-google-street-view"
              >Publicar un escaneo o tour con Travvir</a
            >
          </li>
          <li>
            <a href="/blog/es/tour-virtual-google-mi-negocio"
              >Tour virtual en Google Business</a
            >
          </li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "tour-virtual-google-street-view",
);
const config = useRuntimeConfig();
const mainSiteUrl = config.public.baseUrl;

useBlogSeo({
  title:
    "Tour Virtual de Google Street View: Crear y Publicar en 2026 | Travvir",
  description:
    "Crea un tour virtual de Google Street View: elige el lugar en Maps y publica un escaneo (con escenas) o un tour 360° directo. Travvir es el editor en esa ubicación.",
  keywords:
    "tour virtual Google Street View, tour virtual Google Maps, 360 Google Business, recorrido virtual Google",
  slug: "tour-virtual-google-street-view",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Cómo publicar un tour virtual en Google Street View?",
      answer:
        "Elige el lugar en Google Maps. En un escaneo único, selecciona escenas y publica; en un tour 360°, publícalo directamente. Travvir es el editor en ese sitio. Google suele aprobar en 24–72 h.",
    },
    {
      question: "¿Un tour de Google Street View mejora el SEO local?",
      answer:
        "Sí — los negocios con tours virtuales de Google registran 2× más engagement en su ficha y una mejor puntuación de notoriedad que mejora su posición en los resultados locales.",
    },
  ],
});

</script>
